import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { GalleryService } from '../gallery.service';

/** One thing the viewer can show. mediaIndex is its place in gs.Images / Clips / Fulls / PDFs. */
export interface ViewerItem {
  kind: 'img' | 'clip' | 'full' | 'pdf';
  src: string;
  thumb?: string;
  label: string;
  group?: string;
  mediaIndex: number;
}

/**
 * The gallery's viewer: images, clips, recordings and PDFs in one modal, so the arrows go from an
 * image to a clip without closing anything (they used to open in three different viewers). It
 * steps through exactly the list it is given, in that order. A PDF is shown by the browser's own
 * PDF reader, as before - print, zoom and search stay where everyone knows them.
 */
@Component({
  selector: 'app-media-viewer',
  templateUrl: './media-viewer.component.html',
  styleUrls: ['./media-viewer.component.scss'],
  standalone: false
})
export class MediaViewerComponent implements OnChanges, OnDestroy {
  @Input() items: ViewerItem[] = [];
  @Input() index = 0;
  /** Delete shows only where the exam's gallery popup offered it before. */
  @Input() deletable = false;
  /** The gallery is deleting: Delete waits for the server, then the gallery hands over the new list. */
  @Input() deleting = false;
  @Input() deleteFailed = false;
  @Output() closed = new EventEmitter<void>();
  /** Delete confirmed for the item on screen. */
  @Output() remove = new EventEmitter<ViewerItem>();
  /** The ? button: the gallery opens its shortcuts. */
  @Output() help = new EventEmitter<void>();
  @ViewChild('root') root: ElementRef<HTMLElement>;
  @ViewChild('strip') strip: ElementRef<HTMLElement>;

  full = false;
  /** Delete was clicked: the bar asks before anything is deleted. */
  confirming = false;
  /** The item on screen has not loaded yet: the loader shows (after a short delay, see the scss). */
  loading = false;
  pdfUrl: SafeResourceUrl = null;
  pdfFailed = false;
  private objectUrl: string = null;
  private shownSrc: string = null;
  private pdfSub: Subscription;

  constructor(private gs: GalleryService, private sanitizer: DomSanitizer) {}

  get item(): ViewerItem { return this.items[this.index]; }
  get isVideo(): boolean { return this.item && (this.item.kind === 'clip' || this.item.kind === 'full'); }

  ngOnChanges(c: SimpleChanges): void {
    // A reloaded list with the same item at this place leaves it on screen as it is: an image
    // whose src does not change never fires load again, so the loader would stay.
    if (!(c['items'] || c['index'])) return;
    // a shorter list (an item deleted at the end) ends on its last item
    this.index = Math.min(this.index, this.items.length - 1);
    if (this.item && this.item.src === this.shownSrc) return;
    this.show(this.index);
  }

  show(i: number): void {
    if (i < 0 || i >= this.items.length) return;
    this.index = i;
    this.shownSrc = this.item.src;
    this.confirming = false;
    this.releasePdf();
    this.loading = true;
    if (this.item.kind === 'pdf') this.loadPdf(this.item.mediaIndex);
    // keep the thumbnail of what is on screen in view
    setTimeout(() => {
      const on = this.strip && this.strip.nativeElement.querySelector('.is-on');
      if (on) on.scrollIntoView({ block: 'nearest', inline: 'center' });
    });
  }
  loaded(): void { this.loading = false; }
  prev(): void { this.show(this.index - 1); }
  next(): void { this.show(this.index + 1); }

  private loadPdf(pdfIndex: number): void {
    this.pdfFailed = false;
    this.pdfSub = this.gs.getPDF(pdfIndex).subscribe(
      (data) => {
        this.objectUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
      },
      (err) => {
        console.log(err);
        this.pdfFailed = true;
        this.loading = false;
      }
    );
  }
  private releasePdf(): void {
    if (this.pdfSub) this.pdfSub.unsubscribe();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.pdfUrl = null;
  }
  get kindName(): string {
    return { img: 'image', clip: 'clip', full: 'recording', pdf: 'document' }[this.item.kind];
  }
  confirmDelete(): void {
    if (!this.deleting) this.remove.emit(this.item);
  }

  openInTab(): void {
    window.open('api/getmedia/' + this.gs.PDFs[this.item.mediaIndex] + '/n', '_blank');
  }

  // Full screen when the browser allows it; where it does not, the viewer still fills the window.
  // Opened from a grid that is in full screen, the viewer sits inside it: leaving or closing the
  // viewer must only end the viewer's own full screen, never the grid's.
  private get ownsFullscreen(): boolean {
    return !!this.root && document.fullscreenElement === this.root.nativeElement;
  }

  toggleFull(): void {
    this.full = !this.full;
    try {
      if (this.full && this.root.nativeElement.requestFullscreen) this.root.nativeElement.requestFullscreen().catch(() => {});
      if (!this.full && this.ownsFullscreen) document.exitFullscreen().catch(() => {});
    } catch (e) { /* filling the window is enough */ }
  }
  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    if (!this.ownsFullscreen) this.full = false;
  }

  close(): void {
    if (this.ownsFullscreen) document.exitFullscreen().catch(() => {});
    this.closed.emit();
  }
  onBackdrop(e: MouseEvent): void {
    if (e.target === this.root.nativeElement) this.close();
  }

  // The arrow keys move between items - on a playing clip they no longer seek inside it.
  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === 'ArrowRight') { this.next(); e.preventDefault(); }
    else if (e.key === 'ArrowLeft') { this.prev(); e.preventDefault(); }
    else if (e.key === 'f' || e.key === 'F') { this.toggleFull(); e.preventDefault(); }
    else if (e.key === 'Escape') {
      if (this.confirming) this.confirming = false; else if (this.full) this.toggleFull(); else this.close();
      e.preventDefault();
    }
  }

  ngOnDestroy(): void {
    this.releasePdf();
    if (this.ownsFullscreen) document.exitFullscreen().catch(() => {});
  }
}
